import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {UserJwt} from '../../models/admin_owner/user/user-jwt';
import {
  ContactChallengeStatus,
  ContactGateStatus,
  ContactLink,
  ContactLinkChallenge,
  ContactLinkPurposeValue,
  ContactLinksResponse,
  readContactVerdict
} from './contact-link.models';

/**
 * Talks to the **monolith's** contact-gate and contact-link endpoints (`/auth/contact-gate/*`,
 * `/auth/contact-link/*`) — the twin of `FormsGateHttpService`, and deliberately shaped like it.
 *
 * Every route here is whitelisted by `ContactGateMiddleware`, i.e. reachable while the session is
 * `restricted`; that is what makes the lockout screen able to lift its own restriction. The `AuthInterceptor`
 * attaches the bearer, and `withCredentials` carries the `CM-Secret` cookie the monolith needs to decrypt it.
 *
 * Note what this service does **not** do: it never interprets a verdict, never decides a route and never
 * touches the session. `readContactVerdict` normalising the wire value is the one liberty it takes, and that
 * is a parsing concern, not a policy one.
 */
@Injectable({providedIn: 'root'})
export class ContactLinkHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  /** The current employee's contact-gate status: verdict, outstanding tasks, existing links, channel catalogue. */
  status(): Observable<ContactGateStatus> {
    return this.http.get<ContactGateStatus>(`${this.baseUrl}/auth/contact-gate/status`, {withCredentials: true})
      .pipe(map(status => ({
        ...status,
        // Normalised once, here, so no screen has to know that the verdict could arrive as an ordinal.
        verdict: readContactVerdict(status?.verdict),
        // The server is the authority on these, but an older/degraded build omitting one must not crash a
        // template that iterates it. Defaulted rather than guarded at ~10 call sites.
        missing: status?.missing ?? [],
        reverifyDue: status?.reverifyDue ?? [],
        links: status?.links ?? [],
        channels: status?.channels ?? []
      })));
  }

  /** Re-evaluate the contact gate and mint a fresh token (live upgrade, no re-login). */
  reissue(): Observable<UserJwt> {
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/contact-gate/reissue`, {}, {withCredentials: true});
  }

  /**
   * Start a link (or re-verification) of one channel.
   *
   * PascalCase body, per this app's convention for request bodies (responses stay camelCase).
   */
  startChallenge(channel: string, purpose: ContactLinkPurposeValue): Observable<ContactLinkChallenge> {
    return this.http.post<ContactLinkChallenge>(`${this.baseUrl}/auth/contact-link/challenge`,
      {Channel: channel, Purpose: purpose}, {withCredentials: true});
  }

  /** Poll one challenge. The wizard calls this on a timer until the state is terminal. */
  challengeStatus(challengeId: string): Observable<ContactChallengeStatus> {
    return this.http.get<ContactChallengeStatus>(
      `${this.baseUrl}/auth/contact-link/challenge/${encodeURIComponent(challengeId)}`, {withCredentials: true});
  }

  /** The signed-in employee's own links, with the employers each is shared with. */
  mine(): Observable<ContactLink[]> {
    return this.http.get<ContactLinksResponse>(`${this.baseUrl}/auth/contact-link/mine`, {withCredentials: true})
      .pipe(map(response => response?.links ?? []));
  }

  /**
   * Drop the employee's own link.
   *
   * Typed `void` because the endpoint answers 200 **or** 204 — reading a body that may not exist is how a
   * successful unlink ends up in an `error` callback.
   */
  unlink(linkId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/auth/contact-link/${encodeURIComponent(linkId)}/unlink`, {}, {withCredentials: true});
  }
}
